import { requestApi } from 'api/api'

export interface CardTransactionsParams {
  pageNumber?: number
  pageSize: number
  q?: string
}

export interface CardsParams {
  q: string
  pageNumber?: number
  pageSize?: number
}

export type CardLimitType = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | 'PER_TRANSACTION'

export interface CardLimitConfigured {
  type: CardLimitType
  value: number
}

export interface CardLimit {
  configured: CardLimitConfigured
  available: number
  nextResetAt: string
}

export interface CardTransactionStats {
  latestAuthorizedAt: string
}

export type CardStatus =
  | 'CREATED'
  | 'PENDING'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'BLOCKED'
  | 'EXPIRED'
  | 'FROZEN'
  | 'CANCELLED'

export type LedgerType =
  | 'BVNK_ACCOUNT_WALLET'
  | 'BVNK_EXPRESS_WALLET'
  | 'BVNK_EMBEDDED_COLLATERAL_WALLET'

export type WalletProvider = 'APPLE_PAY' | 'GOOGLE_PAY' | 'OTHER'

export interface ProvisionedWallet {
  walletProvider: WalletProvider
  provisionedAt: string
}

export type CardAuditActorType = 'ADMIN' | 'PARTNER' | 'CUSTOMER' | 'SYSTEM'

export interface StatusChangeMetadata {
  type: 'STATUS_CHANGE'
  newStatus: CardStatus
}

export interface LimitChangeMetadata {
  type: 'LIMIT_CHANGE'
  newLimit: CardLimitConfigured
}

export type CardAuditMetadata = StatusChangeMetadata | LimitChangeMetadata

export interface CardAudit {
  id: string
  metadata: CardAuditMetadata
  reason?: string
  actorName: string
  actorType: CardAuditActorType
  createdAt: string
}

export interface Card {
  id: string
  customerId: string
  assetCode: string
  last4Digits: string
  status: CardStatus
  createdAt: string
  updatedAt: string
  expiresAt: string
  limit: CardLimit
  ledger: {
    id: string
    type: LedgerType
  }
  cardTransactionStats: CardTransactionStats
  provisionedWallets?: ProvisionedWallet[]
  audit?: CardAudit[]
}

export type CardTransactionStatus = 'PENDING' | 'AUTHORIZED' | 'SETTLED' | 'DECLINED' | 'CANCELLED'

export interface CardProviderTransactionLog {
  id: string
  amount: number
  asset: string
  exchangeRate: number
  providerType: 'RAIN' | string
  rawTransaction: Record<string, string>
}

export interface CardTransactionMetadata {
  merchantCategory?: string
  merchantName?: string
  merchantCategoryCode?: string
  merchantCity?: string
  merchantCountry?: string
  localCurrency?: string
  localAmount?: number
  usdAmount?: number
}

export interface CardTransactionReportData {
  interchangeAmount?: number
  fxFee?: number
  isThreeDSecureTransaction?: boolean
  isInternationalTransaction?: boolean
}

export interface CardTransaction {
  id: string
  cardProviderTransactionId: string
  cardId: string
  cardNumberLast4Digits: string
  authorizedAmount: number
  settledAmount: number
  asset: string
  ledgerProviderTransactionIds: Record<string, string[]>
  status: CardTransactionStatus
  createdAt: string
  updatedAt: string
  cardProviderTransactionLogs: CardProviderTransactionLog[]
  metadata: CardTransactionMetadata
  reportData?: CardTransactionReportData
  formattedDeclineReason?: string
}

export interface Pageable {
  pageNumber: number
  pageSize: number
}

export interface CardTransactionsResponse {
  content: CardTransaction[]
  pageable: Pageable
  totalElements: number
}

export const getCardTransactions = async (
  params: CardTransactionsParams
): Promise<CardTransactionsResponse> => {
  const queryParams: Record<string, string | number> = {
    pageNumber: params.pageNumber ?? 0,
    pageSize: params.pageSize,
  }

  if (params.q) {
    queryParams.q = params.q
  }

  return requestApi({
    url: '/card/v1/card-transactions',
    method: 'GET',
    params: queryParams,
    baseUrl: process.env.API_URL,
  })
}

export interface CardsResponse {
  content: Card[]
  pageable: Pageable
  totalElements: number
}

export const getCards = async (params: CardsParams): Promise<CardsResponse> => {
  return requestApi({
    url: '/card/v1/cards',
    method: 'GET',
    params: {
      q: params.q,
      pageNumber: params.pageNumber ?? 0,
      pageSize: params.pageSize ?? 50,
    },
    baseUrl: process.env.API_URL,
  })
}

export interface UpdateCardStatusParams {
  cardId: string
  newStatus: CardStatus
  reason?: string
}

export const updateCardStatus = ({
  cardId,
  newStatus,
  reason,
}: UpdateCardStatusParams): Promise<Card> => {
  return requestApi({
    url: `/card/v1/cards/${cardId}/status`,
    method: 'PATCH',
    data: { newStatus, ...(reason ? { reason } : {}) },
    baseUrl: process.env.API_URL,
  })
}
